import React, {useState, useCallback, useEffect, useMemo} from 'react'
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import {ObjectTypes} from '../../api/consts';
import Api from '../../api/SMPFetchHelper';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import connectedLogo from '../../assets/images/gateway/connected.png';
import disconnectedLogo from '../../assets/images/gateway/disconnected.png';
import disabledLogo from '../../assets/images/gateway/disabled.png';
import SMCLabel from '../../components/Simple/SMCLabel';
import {GatewayGetListSchema, GatewayFilterSchema, GatewayConnectedStatuses} from '../../api/DBSchema/Gateway';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';

const gatewayStatusCell = React.memo(({value}) => {
    switch (value) {
        case GatewayConnectedStatuses.connected:
            return <img src={connectedLogo} alt="connected" />;
        case GatewayConnectedStatuses.disconnected:
            return <img src={disconnectedLogo} alt="disconnected" />;
        case GatewayConnectedStatuses.disabled:
            return <img src={disabledLogo} alt="disabled" />;
        default:
            return null;
    }
});

function SetGatewayModal({
    isOpen,
    onRequestClose,
    onSuccess,
    disableSelectById,
    selectMultiple
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();

    const gatewayTableColumns = useMemo(() => ([
        { id: 'status',           accessor: GatewayGetListSchema.status,           header: '',                                  width: 25,  align: 'center', Cell: gatewayStatusCell },
        { id: 'name',             accessor: GatewayGetListSchema.name,             header: t('SMP.NAME'),                       width: 150,                                          },
        { id: 'description',      accessor: GatewayGetListSchema.description,      header: t('SMP.DESCRIPTION'),                width: 140,                                          },
        { id: 'ipAddress',        accessor: GatewayGetListSchema.gatewayIp,        header: t('SMC_GATEWAYNET_IP'),              width: 115,                                          },
        { id: 'macAddress',       accessor: GatewayGetListSchema.macAddress,       header: t('SMP.MAC_ADDRESS'),                width: 130,                                          },
        { id: 'owner',            accessor: GatewayGetListSchema.ownerId,          header: t('SMC_GATEWAY_OWNER'),              width: 115,                                          },
        { id: 'gatewayType',      accessor: GatewayGetListSchema.gatewayType,      header: t('SMC_GATEWAYS_TYPE'),              width: 160,                                          },
        { id: 'reportedFirmware', accessor: GatewayGetListSchema.reportedFirmware, header: t('SMC_GATEWAYS_REPORTED_FIRMWARE'), width: 155,                                          },
        { id: 'plan',             accessor: GatewayGetListSchema.planId,           header: t('SMC_GATEWAYS_PLAN'),              width: 110,                                          }
    ]), [t]);

    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [gatewayTableData, setGatewayTableData] = useState([]);
    const [gatewaySeachText, setGatewaySeachText] = useState('');
    const [errorMessage, setErrorMessage] = useState(null);

    const handleTableSelect = useCallback((selectedObjects) => {
        let newSelectedObjects = [...selectedObjects];
        if (!selectMultiple) {
            // If selected 2, and only alowed one, replace the originally selected with the new one
            if (newSelectedObjects.length === 2) {
                newSelectedObjects = [newSelectedObjects[1]]
            }
            // Not alowed multiselect
            else if (newSelectedObjects.length > 2) {
                return;
            }
        }
        setSelectedObjects(newSelectedObjects);
    }, [selectMultiple]);

    const loadData = useCallback(() => {
        setIsTableDataLoading(true);
        Api.fetchObjectList(ObjectTypes.GATEWAY, 0, null, null, null, {[GatewayFilterSchema.freeTextSearch]: {value: gatewaySeachText}})
        .then((resp) => {
            setGatewayTableData(resp.data.data);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        })
        .finally(() => {
            setIsTableDataLoading(false);
        });
    }, [gatewaySeachText, getErrorMessage]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Reset values on open
    useEffect(() => {
        if (isOpen) {
            setSelectedObjects([])
            setGatewaySeachText('')
        }
    }, [isOpen]);

    const handleSetGateway = useCallback(() => {
        if (selectedObjects.length > 0) {
            onSuccess(selectedObjects);
        }
    }, [selectedObjects, onSuccess]);

    return (
        <SMCModal
            title="Set gateway"
            height={620}
            width={690}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            errorMessage={errorMessage}
            reloadData={loadData}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMC_GATEWAYWIZ_CHOOSE')}</SMCLabel>
                <SMCTextInput
                    suffixIcon="search"
                    allowClearAll
                    placeholder={t('SMP.SEARCH')}
                    value={gatewaySeachText}
                    onChange={e => setGatewaySeachText(e.target.value)}
                />
                <SMCTable
                    loading={isTableDataLoading}
                    content={gatewayTableData}
                    columns={gatewayTableColumns}
                    width={630}
                    height={350}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={handleTableSelect}
                    rowIdSelector={obj => obj[GatewayGetListSchema.name]}
                    isRowSelectable={obj => Array.isArray(disableSelectById) ? !disableSelectById.includes(obj[GatewayGetListSchema.name]) : true}
                    hasCheckAll={true}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleSetGateway} disabled={selectedObjects.length === 0}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    Set gateway
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default SetGatewayModal;